import { createElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { FoodsIcon } from "@foodpilot/foods";
import { MRT_ColumnDef } from "material-react-table";

import { useGridColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { useGetAllSectors } from "src/api/client-api/companySectors/useSupplierSectors";
import { CREATE, DELETE, MODIFY, PUCK_PAGE, useFoodpilotContext } from "src/context/FoodpilotContext";
import { Score } from "src/types";
import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";

import { getPageRoles } from "src/pages/CMS/utils/roles";
import { useRoles } from "src/api/client-api/roles/useRoles";
import { useTitleEditing } from "./useTitleEditing";
import { usePageDialogs } from "./usePageDialogs";
import { buildPagesTableColumns } from "../components/columns/buildCMSEditorColumns.tsx";

export type CMSEditorPagesTableControllerArgs = {
    pages: PuckPage[];
    onEdit: (slug: string) => void;
    getEditUrl?: (slug: string) => string;
    pageType?: "CMS" | "PDF";
    onDelete: (slug: string) => void;
    onUpdateTitle: (slug: string, title: string) => void;
    onUpdateRoles?: (slug: string, roles: string[]) => void;
    onUpdateStatus?: (slug: string, status: "active" | "draft") => void;
    onUpdateMeta?: (
        slug: string,
        payload: {
            url?: string;
            sectors?: string[] | null;
            allowedRoles?: string[];
            status?: "active" | "draft";
            title?: string;
            description?: string;
            downloadOnlyOnSsqValidated?: boolean;
            score?: string | string[];
            entity?: string | string[];
        },
    ) => Promise<unknown> | void;
    onDuplicatePage?: (source: PuckPage, payload: { title: string; url: string }) => Promise<unknown> | void;
    isServerSide: boolean;
    isCreating?: boolean;
};

export const useCMSEditorPagesTableController = (args: CMSEditorPagesTableControllerArgs) => {
    const {
        onEdit,
        getEditUrl,
        pageType = "CMS",
        onDelete,
        onUpdateTitle,
        onUpdateRoles,
        onUpdateStatus,
        onUpdateMeta,
        onDuplicatePage,
        isServerSide,
        isCreating,
    } = args;

    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();

    const cmsEditorPagesTableKey = "cms-editor-pages-table";

    const { companySectors: allSectors } = useGetAllSectors({ itemsPerPage: 100 });

    const foodpilotContext = useFoodpilotContext();
    const canCreatePage = foodpilotContext.canUser(CREATE, PUCK_PAGE);
    const canModifyPage = foodpilotContext.canUser(MODIFY, PUCK_PAGE);
    const canDeletePage = foodpilotContext.canUser(DELETE, PUCK_PAGE);
    const { getRoleDisplayName } = useRoles();
    const scores: Score[] =
        foodpilotContext?.getScoresByType ?
            foodpilotContext.getScoresByType("company")
        :   (foodpilotContext?.scores ?? []);

    const scoreLabelById = useMemo(() => {
        const map: Record<string, string> = {};
        scores.forEach((s) => {
            map[String(s.id)] = s.title;
        });
        return map;
    }, [scores]);

    const entityLabelById = useMemo(() => {
        return {
            company: t("entity.company", "Company"),
            product: t("entity.product", "Product"),
            ingredient: t("entity.ingredient", "Ingredient"),
            packaging: t("entity.packaging", "Packaging"),
            supplier: t("entity.supplier", "Supplier"),
            productline: t("entity.product_line", "Product line"),
        };
    }, [t]);

    const titleEditing = useTitleEditing({ onUpdateTitle });

    const dialogs = usePageDialogs({
        pageType,
        onUpdateMeta,
        onUpdateRoles,
        onDuplicatePage,
        isCreating,
    });

    const columns = useMemo<MRT_ColumnDef<PuckPage>[]>(
        () =>
            buildPagesTableColumns({
                t,
                isServerSide,
                editingTitle: titleEditing.editingTitle,
                isTitleUpdating: titleEditing.isTitleUpdating,
                handleSaveTitle: titleEditing.saveTitle,
                setEditingTitle: titleEditing.setEditingTitle,
                onEdit,
                getEditUrl,
                pageType,
                scoreLabelById,
                companyLabelById: entityLabelById,
                getPageRoles,
                getRoleDisplayName,
                handleOpenRolesDialog: dialogs.openRolesDialog,
                handleOpenScoreCompanyDialog: pageType === "PDF" ? dialogs.openScoreCompanyDialog : undefined,
                handleOpenSettingsDialog: dialogs.openSettingsDialog,
                onUpdateStatus,
                allSectors,
                theme,
                navigate: (url: string) => navigate(url),
                canModifyPage,
            }),
        [
            t,
            isServerSide,
            titleEditing.editingTitle,
            titleEditing.isTitleUpdating,
            titleEditing.saveTitle,
            titleEditing.setEditingTitle,
            onEdit,
            getEditUrl,
            pageType,
            scoreLabelById,
            entityLabelById,
            getRoleDisplayName,
            dialogs.openRolesDialog,
            dialogs.openScoreCompanyDialog,
            dialogs.openSettingsDialog,
            onUpdateStatus,
            allSectors,
            theme,
            navigate,
            canModifyPage,
        ],
    );

    const menuItems = useMemo(() => {
        return [
            ...(canModifyPage ?
                [
                    {
                        key: "edit",
                        label: t("common.actions.edit", "Edit"),
                        icon: createElement(FoodsIcon, { icon: "edit", size: 2 }),
                        onClick: (item: PuckPage) => onEdit(item.slug),
                    },
                ]
            :   []),
            ...(canDeletePage ?
                [
                    {
                        key: "delete",
                        label: t("common.actions.delete", "Delete"),
                        icon: createElement(FoodsIcon, { icon: "delete", size: 2 }),
                        onClick: (item: PuckPage) => onDelete(item.slug),
                        divider: true,
                    },
                ]
            :   []),
            ...(canCreatePage ?
                [
                    {
                        key: "duplicate",
                        label: t("common.actions.duplicate", "Duplicate"),
                        icon: createElement(ContentCopy, { fontSize: "small" }),
                        onClick: (item: PuckPage) => dialogs.openDuplicateDialog(item),
                        divider: true,
                    },
                ]
            :   []),
            ...(canModifyPage ?
                [
                    {
                        key: "settings",
                        label: t("common.actions.settings", "Settings"),
                        icon: createElement(FoodsIcon, { icon: "settings", size: 2 }),
                        onClick: (item: PuckPage) => dialogs.openSettingsDialog(item),
                    },
                    {
                        key: "rename",
                        label: t("common.actions.change_title", "Change title"),
                        icon: createElement(FoodsIcon, { icon: "notes", size: 2 }),
                        onClick: (item: PuckPage) => titleEditing.startEditing(item.slug, item.title),
                    },
                    {
                        key: "roles",
                        label: t("settings.cms.pages.table.roles", "Roles"),
                        icon: createElement(FoodsIcon, { icon: "viewPwd", size: 2 }),
                        onClick: (item: PuckPage) => dialogs.openRolesDialog(item),
                    },
                ]
            :   []),
        ];
    }, [canCreatePage, canDeletePage, canModifyPage, dialogs, onDelete, onEdit, t, titleEditing]);

    const mrt_columns = useGridColumns(columns, cmsEditorPagesTableKey);

    return {
        theme,
        cmsEditorPagesTableKey,
        emptyMessage: t("settings.cms.pages.no_pages", "No pages found"),
        menuItems,
        mrt_columns,
        rolesDialog: dialogs.rolesDialog,
        scoreCompanyDialog: {
            ...dialogs.scoreCompanyDialog,
            scores,
        },
        settingsDialog: {
            ...dialogs.settingsDialog,
            allSectors,
            scores,
            urlHelperText:
                dialogs.settingsDialog.urlError ?
                    t("settings.cms.pages.create_dialog.url_invalid", "URL is invalid or reserved")
                :   "",
        },
        duplicateDialog: dialogs.duplicateDialog,
        isPdfTemplates: pageType === "PDF",
        canModifyPage,
    };
};
